<template>
  <div class="ibuzzpen-page">
    <AppNavbar active-tab="iBuzzPen" theme="light" />

    <!-- Product Section -->
    <section class="product-section">
      <div class="product-inner">
        <!-- Gallery -->
        <div class="gallery">
          <div class="main-preview">
            <img :src="currentImage" alt="iBuzzPen" />
          </div>
          <div class="thumb-row">
            <div
              v-for="(img, index) in galleryImages"
              :key="index"
              class="thumb-item"
              :class="{ active: currentIndex === index }"
              @click="currentIndex = index"
            >
              <img :src="img" :alt="'iBuzzPen view ' + (index + 1)" />
            </div>
          </div>
        </div>

        <!-- Product Info -->
        <div class="product-info">
          <span class="offer-tag">Pre-order offer</span>
          <h1 class="product-title">iBuzzPen</h1>
          <div class="price-row">
            <span class="price-now">$299</span>
            <span class="price-old">$399</span>
            <span class="price-save">Save 100</span>
          </div>
          <p class="product-desc">
            iBuzznote Smart Pen, a note-taker, a thought organizer, and an emotion-interpreter.
            Supporting Notebooks &amp; Subscription Services
          </p>

          <ul class="feature-list">
            <li>
              <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="#2fa84f" stroke-width="3">
                <polyline points="20,6 9,17 4,12" />
              </svg>
              <div class="feature-text">
                <span class="feature-title">Digitize From Customize Paper</span>
                <span class="feature-sub">
                  You can purchase specialized handwriting paper and bind it to your favorite notebook,
                  allowing you to write on any notebook. You can also buy our pen and notebook package.
                </span>
              </div>
            </li>
            <li>
              <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="#2fa84f" stroke-width="3">
                <polyline points="20,6 9,17 4,12" />
              </svg>
              <div class="feature-text">
                <span class="feature-title">Analyze Your Handwriting</span>
              </div>
            </li>
            <li v-if="showMore">
              <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="#2fa84f" stroke-width="3">
                <polyline points="20,6 9,17 4,12" />
              </svg>
              <div class="feature-text">
                <span class="feature-title">Emotion Interpretation</span>
                <span class="feature-sub">Understand your emotional state from the way you write.</span>
              </div>
            </li>
          </ul>
          <button class="show-more-btn" @click="showMore = !showMore">
            Show {{ showMore ? 'less' : 'more' }}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
              :style="{ transform: showMore ? 'rotate(180deg)' : 'none' }">
              <polyline points="6,9 12,15 18,9" />
            </svg>
          </button>

          <div class="service-row">
            <div class="service-item" v-for="(s, i) in services" :key="i">
              <img class="service-icon" :src="s.icon" :alt="s.title" />
              <div class="service-text">
                <span class="service-title">{{ s.title }}</span>
                <span class="service-sub">{{ s.sub }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Join Waitlist -->
    <section class="waitlist-section">
      <h2 class="waitlist-title">Join waitlist</h2>
      <p class="waitlist-sub">Our last pre-order batch sold out. Secure your spot by signing up today.</p>
      <div class="waitlist-form">
        <input
          type="email"
          v-model="waitlistEmail"
          placeholder="E-mail"
          class="waitlist-input"
          @keyup.enter="submitWaitlist"
        />
        <button class="waitlist-btn" :disabled="waitlistSubmitting" @click="submitWaitlist">
          <img class="btn-mail-icon" src="@/assets/iBuzzPen/email-icon.png" alt="" />
          {{ waitlistSubmitting ? 'Submitting...' : 'Notify Me' }}
        </button>
      </div>
      <p v-if="waitlistTip" class="waitlist-tip" :class="{ ok: waitlistTipOk }">{{ waitlistTip }}</p>
    </section>

    <!-- Feature Icons -->
    <section class="icons-section">
      <span class="icons-brand">iBuzzPen</span>
      <h2 class="section-title">The Smart Pen For Supprting<br />Notebooks &amp; Subscription<br />Services</h2>
      <div class="icons-grid">
        <div class="icon-cell" v-for="(item, i) in iconFeatures" :key="i">
          <img class="cell-icon" :src="item.icon" :alt="item.label" />
          <span class="cell-label">{{ item.label }}</span>
        </div>
      </div>
    </section>

    <!-- Video Section -->
    <section class="video-section">
      <div class="video-wrap">
        <!-- <video
          ref="videoRef"
          class="video-player"
          :src="videoUrl"
          preload="metadata"
          playsinline
          :controls="playing"
          controlsList="nodownload"
          @ended="playing = false"
          @pause="playing = false"
          @play="playing = true"
        ></video>
        <div v-if="!playing" class="video-overlay" @click="togglePlay">
          <img class="play-btn" src="@/assets/OurStory/play.png" alt="Play" />
          <span class="play-text">2mins get to know about us</span>
        </div> -->

        <div v-if="!playing" class="video-overlay">
          <img class="play-btn" src="@/assets/OurStory/play.png" alt="Play" />
          <span class="play-text">2mins get to know about us</span>
        </div>
      </div>
    </section>

    <!-- Grip Section -->
    <section class="grip-section">
      <h2 class="grip-title">Engineered for a Perfect Grip</h2>
      <p class="grip-sub">10.75mm in diameter</p>
      <img class="grip-img" src="@/assets/iBuzzPen/6.png" alt="iBuzzPen dimensions" />
    </section>

    <!-- Smart Pen Cards -->
    <section class="cards-section">
      <span class="cards-eyebrow">The Bridge Between Mind and Digital</span>
      <h2 class="section-title">All-in-One Smart Pen:<br />Note-Taking&amp; Emotion Sensing</h2>
      <p class="section-sub">Bring together what you write, hear, think, and more importantly, what you feel.</p>
      <div class="cards-grid">
        <div class="smart-card" v-for="(card, i) in smartCards" :key="i">
          <img class="smart-card-img" :src="card.img" :alt="card.title" />
          <h3 class="smart-card-title">{{ card.title }}</h3>
          <p class="smart-card-desc">{{ card.desc }}</p>
        </div>
      </div>
    </section>

    <!-- Specs Section -->
    <section class="specs-section">
      <h2 class="section-title">Precision Engineered for<br />Performance</h2>
      <p class="section-sub specs-sub">
        Sleek, lightweight, and intuitively designed to capture your thoughts seamlessly.
        Advanced sensors and enduring battery life work in harmony to elevate every writing session.
      </p>
      <div class="specs-table">
        <div class="spec-row" v-for="(row, i) in specs" :key="i">
          <span class="spec-key">{{ row.key }}</span>
          <span class="spec-value">
            <span class="spec-value-line" v-for="(line, j) in row.values" :key="j">{{ line }}</span>
          </span>
        </div>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import axios from 'axios'
import AppNavbar from '@/components/AppNavbar.vue'
import AppFooter from '@/components/AppFooter.vue'

export default {
  name: 'IBuzzPen',
  components: { AppNavbar, AppFooter },
  data() {
    return {
      waitlistEmail: '',
      waitlistSubmitting: false,
      waitlistTip: '',
      waitlistTipOk: false,
      // 商品图集: 默认显示第一张
      currentIndex: 0,
      galleryImages: [
        require('@/assets/iBuzzPen/section_1.jpg'),
        require('@/assets/iBuzzPen/section_1.png'),
        require('@/assets/iBuzzPen/section_2.jpg'),
        require('@/assets/iBuzzPen/section_3.jpg'),
        require('@/assets/iBuzzPen/section_3.png'),
        require('@/assets/iBuzzPen/section_4.jpg'),
        require('@/assets/iBuzzPen/section_4.png')
      ],
      showMore: false,
      services: [
        {
          icon: require('@/assets/iBuzzPen/iBuzzPen.png'),
          title: 'Seamlessly Integrated App',
          sub: 'Transcribe, search, and manage your notes in the iBuzzPen App. Includes AI Assistant and unlimited cloud storage.'
        },
        {
          icon: require('@/assets/iBuzzPen/(1).png'),
          title: 'Continuous Updates',
          sub: 'Receive regular software updates with new features and improvements, ensuring your iBuzzPen gets smarter over time.'
        },
        {
          icon: require('@/assets/iBuzzPen/(2).png'),
          title: 'Priority Shipping',
          sub: 'Your iBuzzPen is shipped with priority service and tracking, ensuring a fast and secure delivery.'
        }
      ],
      iconFeatures: [
        { icon: require('@/assets/iBuzzPen/icon1.png'), label: 'Real-Time Digital Sync' },
        { icon: require('@/assets/iBuzzPen/icon2.png'), label: 'Dual-Track Transcription' },
        { icon: require('@/assets/iBuzzPen/icon3.png'), label: 'AI-Powered Summaries' },
        { icon: require('@/assets/iBuzzPen/icon4.png'), label: 'Emotional Intelligence' },
        { icon: require('@/assets/iBuzzPen/icon5.png'), label: 'Screen-Free Focus' },
        { icon: require('@/assets/iBuzzPen/icon6.png'), label: 'Weeks-Long Battery' }
      ],
      smartCards: [
        {
          img: require('@/assets/iBuzzPen/yanshi1.png'),
          title: 'Emotion & Stress Monitoring',
          desc: 'Based on your personal wellness data, the tool delivers tailored tips to recharge, relax and reset—gentle reminders to step away, breathe and rest when you need it most. Beat constant stress and burnout, find your perfect rhythm, and strike a lasting balance between top-tier productivity and long-term health. Finally, work smarter and live better, all at once.'
        },
        {
          img: require('@/assets/iBuzzPen/yanshi2.png'),
          title: 'Instant Paper Sync',
          desc: 'What you write on paper, appears on your screen instantly. No delay, no manual upload. Just natural writing, mirrored in real time. Replay your strokes, capture your thinking process, and keep every idea safe — in both worlds.'
        },
        {
          img: require('@/assets/iBuzzPen/yanshi3.png'),
          title: 'Multi-Track Audio-Image-Text Transcription',
          desc: 'Turn handwritten sparks of inspiration, live meeting notes and in-class scribbles into digital files instantly. Capture audio recordings, clear whiteboard content and full presentation slides all at the same time, breaking down every data barrier. Finally, handwriting, audio, images and documents live in one unified, easy-to-access hub—no more jumping between apps, notebooks and devices.'
        },
        {
          img: require('@/assets/iBuzzPen/yanshi4.png'),
          title: 'Offline Ready-to-Write',
          desc: 'Simply uncap the iBuzzPen and apply gentle pressure to activate it automatically — no smartphone required. All your handwritten strokes are saved directly to the pen’s built-in memory for fully offline use. Once you connect to the app later, your notes sync seamlessly without interrupting AI transcription, emotion analysis, or wellness reports.'
        }
      ],
      specs: [
        { key: 'Product Size and Weight', values: ['L: 153mm, W: 13.17mm (excluding cap)', '15g (excluding cap)'] },
        { key: 'Battery', values: ['170mAh Polymer Lithium-ion Battery', 'with Safe Power-saving Technology'] },
        { key: 'Rated Input', values: ['DC 5V 170mA'] },
        { key: 'Charging Time', values: ['Approx. 1.5 hours'] },
        { key: 'Working Time', values: ['Approx. 9 hours'] },
        { key: 'Standby Time', values: ['300 days'] },
        { key: 'Storage Medium', values: ['16MB MFLASH'] },
        { key: 'Stroke Storage Mode', values: ['Sequential Storage'] },
        { key: 'Supported OS Platforms', values: ['Multi-system Compatible'] },
        { key: 'Connection Method', values: ['Bluetooth'] },
        { key: 'Transmission Mode', values: ['Wireless Transmission'] }
      ],
      
      videoUrl: 'https://miaobee.com/miaobiAdmin/api/share/getStream?uuid=25687696-2944-4cee-bcbf-adc3453b0062',
      playing: false
    }
  },
  computed: {
    currentImage() {
      return this.galleryImages[this.currentIndex]
    }
  },
  methods: {
    async submitWaitlist() {
      if (this.waitlistSubmitting) return
      if (!this.waitlistEmail) {
        this.waitlistTipOk = false
        this.waitlistTip = 'Please enter your email.'
        return
      }
      if (!/^[a-zA-Z0-9_\-+.]+@[a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)+$/.test(this.waitlistEmail)) {
        this.waitlistTipOk = false
        this.waitlistTip = 'Invalid email format.'
        return
      }
      this.waitlistSubmitting = true
      this.waitlistTip = ''
      try {
        await axios.post(`${this.BaseUrl}/api/ana/submitFeedback`, {
          joinMail: this.waitlistEmail,
          joinType: '1'
        })
        this.waitlistTipOk = true
        this.waitlistTip = 'Subscribed successfully. Thank you!'
        this.waitlistEmail = ''
      } catch (e) {
        this.waitlistTipOk = false
        this.waitlistTip = 'Submission failed, please try again later.'
      } finally {
        this.waitlistSubmitting = false
      }
    },
    togglePlay() {
      if (!this.videoUrl) return
      const video = this.$refs.videoRef
      if (this.playing) {
        video.pause()
        this.playing = false
      } else {
        video.play()
        this.playing = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ibuzzpen-page {
  background: #fff;
}

// ========== Product Section ==========
.product-section {
  padding: 7.3vw 12.5% 4vw;
}

.product-inner {
  display: flex;
  gap: 4vw;
  align-items: flex-start;
}

// Gallery
.gallery {
  flex: 0 0 40%;

  .main-preview {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    background: #fff;
    border-radius: 16px;
    border: 1px solid #EEEEEE;
    border-radius: 0.42vw;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    box-sizing: border-box;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .thumb-row {
    display: flex;
    gap: 0.83vw;
    margin-top: 0.83vw;
  }

  .thumb-item {
    width: 3.5vw;
    height: 3.5vw;
    background: #f6f6f6;
    border: 2px solid transparent;
    border-radius: 0.42vw;
    overflow: hidden;
    cursor: pointer;
    box-sizing: border-box;
    transition: border-color 0.2s;

    &.active {
      border-color: #2fa84f;
      background: #fff;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}

// Product info
.product-info {
  flex: 1;
  padding-top: 0;
}

.offer-tag {
  display: inline-block;
  padding: 0.26vw 0.73vw;
  background: #f0f0f0;
  border-radius: 1vw;
  font-size: 0.68vw;
  color: #777;
  margin-bottom: 0.9vw;
}

.product-title {
  font-size: 2.5vw;
  font-weight: 800;
  color: #111;
  margin: 0 0 0.6vw;
  letter-spacing: -0.02em;
}

.price-row {
  display: flex;
  align-items: center;
  gap: 0.6vw;
  margin-bottom: 1.2vw;

  .price-now {
    font-size: 1.15vw;
    font-weight: 600;
    color: #e6435f;
  }

  .price-old {
    font-size: 0.83vw;
    color: #b0b0b0;
    text-decoration: line-through;
  }

  .price-save {
    padding: 0.16vw 0.57vw;
    background: #e6435f;
    border-radius: 0.6vw;
    font-size: 0.68vw;
    font-weight: 600;
    color: #fff;
  }
}

.product-desc {
  font-size: 0.83vw;
  line-height: 1.8;
  color: #333;
  margin: 0 0 1.6vw;
}

.feature-list {
  list-style: none;
  margin: 0 0 1vw;
  padding: 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.6vw;
    margin-bottom: 1.2vw;
  }

  .check-icon {
    width: 0.83vw;
    height: 0.83vw;
    flex-shrink: 0;
    margin-top: 0.15vw;
  }

  .feature-text {
    display: flex;
    flex-direction: column;
    gap: 0.35vw;
  }

  .feature-title {
    font-size: 0.89vw;
    font-weight: 700;
    color: #191919;
  }

  .feature-sub {
    font-size: 0.78vw;
    line-height: 1.7;
    color: #999;
  }
}

.show-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3vw;
  background: none;
  border: none;
  padding: 0;
  font-size: 0.83vw;
  font-weight: 700;
  color: #191919;
  cursor: pointer;
  margin-bottom: 1.6vw;
}

.service-row {
  display: flex;
  gap: 2vw;
  border-top: 1px solid #eee;
  padding-top: 1.6vw;
}

.service-item {
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 0.7vw;

  .service-icon {
    width: 2.2vw;
    height: 2.2vw;
    object-fit: contain;
    flex-shrink: 0;
  }

  .service-text {
    display: flex;
    flex-direction: column;
    gap: 0.35vw;
  }

  .service-title {
    font-size: 0.89vw;
    font-weight: 700;
    color: #191919;
  }

  .service-sub {
    font-size: 0.73vw;
    line-height: 1.7;
    color: #999;
  }
}

// ========== Waitlist ==========
.waitlist-section {
  background: #fff;
  padding: 2vw 12.5% 3vw;
  text-align: center;
}

.waitlist-title {
  font-size: 2.08vw;
  font-weight: 800;
  color: #111;
  margin: 0 0 1vw;
  letter-spacing: -0.02em;
}

.waitlist-sub {
  font-size: 0.78vw;
  color: #999;
  margin: 0 0 1.8vw;
}

.waitlist-form {
  display: flex;
  justify-content: center;
  gap: 0.83vw;
}

.waitlist-input {
  width: 18vw;
  padding: 0.73vw 1.25vw;
  border: none;
  background: #f4f4f5;
  border-radius: 2vw;
  font-size: 0.78vw;
  outline: none;
  box-sizing: border-box;

  &::placeholder { color: #b0b0b0; }
}

.waitlist-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4vw;
  padding: 0.73vw 1.8vw;
  background: #1b1b2a;
  color: #fff;
  border: none;
  border-radius: 2vw;
  font-size: 0.78vw;
  font-weight: 600;
  cursor: pointer;

  .btn-mail-icon {
    width: 0.94vw;
    height: 0.94vw;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }

  &:hover { background: #2a2a3d; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.waitlist-tip {
  margin: 1vw 0 0;
  font-size: 0.78vw;
  color: #e6435f;

  &.ok { color: #10aa32; }
}

// ========== Section titles ==========
.section-title {
  font-size: 2.29vw;
  font-weight: 800;
  color: #111;
  text-align: center;
  line-height: 1.35;
  letter-spacing: -0.02em;
  margin: 0 0 1vw;
}

.section-sub {
  font-size: 0.94vw;
  color: #777;
  text-align: center;
  margin: 0 0 2.5vw;
}

// ========== Icons Section ==========
.icons-section {
  padding: 3vw 12.5% 4vw;
  text-align: center;
}

.icons-brand {
  display: block;
  font-size: 0.83vw;
  font-weight: 700;
  color: #191919;
  margin-bottom: 1.2vw;
}

.icons-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4.5vw 2vw;
  max-width: 62vw;
  margin: 4.5vw auto 0;
}

.icon-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1vw;

  .cell-icon {
    width: 2.2vw;
    height: 2.2vw;
    object-fit: contain;
  }

  .cell-label {
    font-size: 0.89vw;
    font-weight: 700;
    color: #191919;
    text-align: center;
  }
}

// ========== Video Section ==========
.video-section {
  padding: 2vw 12.5% 3.65vw;
}

.video-wrap {
  position: relative;
  width: 100%;
  height: 30vw;
  border-radius: 1.25vw;
  overflow: hidden;
  background: #000;
  cursor: pointer;
}

.video-player {
  display: block;
  width: 100%;
  height: auto;
  min-height: 30vw;
}

.video-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5vw;
  background-color: rgba(0, 0, 0, 0.5);
  background: url('@/assets/OurStory/video-cover.png') center / cover no-repeat;

  .play-btn {
    width: 4.5vw;
    height: auto;
  }

  .play-text {
    color: #FFFFFF;
    font-size: 2.5vw;
    font-weight: bold;
    line-height: 3.6458vw;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }
}

// ========== Grip Section ==========
.grip-section {
  background: linear-gradient(180deg, #06030b 0%, #1c1430 100%);
  padding: 4.5vw 12.5% 3vw;
  text-align: center;

  .grip-title {
    font-size: 2.29vw;
    font-weight: 700;
    color: #fff;
    margin: 0 0 1.2vw;
  }

  .grip-sub {
    font-size: 1.15vw;
    color: #b8b8c8;
    margin: 0 0 2.5vw;
  }

  .grip-img {
    display: block;
    width: 100%;
    max-width: 56vw;
    margin: 0 auto;
  }
}

// ========== Smart Cards ==========
.cards-section {
  background: #fff;
  padding: 4vw 12.5%;
  text-align: center;
}

.cards-eyebrow {
  display: block;
  font-size: 0.94vw;
  color: #333;
  margin-bottom: 1.5vw;
}

.cards-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3vw 2vw;
  margin-top: 3vw;
  text-align: left;
}

.smart-card {
  overflow: hidden;

  .smart-card-img {
    display: block;
    width: 100%;
    height: auto;
  }

  .smart-card-title {
    font-size: 1.04vw;
    font-weight: 700;
    margin: 1.2vw 0 0.6vw;
    background: linear-gradient(90deg, #10aa32 0%, #07040d 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .smart-card-desc {
    font-size: 0.83vw;
    color: #777;
    line-height: 1.8;
    margin: 0;
  }
}

// ========== Specs ==========
.specs-section {
  padding: 4vw 12.5% 5vw;
}

.specs-sub {
  max-width: 52vw;
  margin: 0 auto 3vw;
  color: #333;
  line-height: 1.8;
}

.specs-table {
  max-width: 31vw;
  margin: 0 auto;
  background: linear-gradient(180deg, #34264c 0%, #06030b 100%);
  border-radius: 0.83vw;
  padding: 1.2vw 1.8vw;
  box-sizing: border-box;
}

.spec-row {
  display: flex;
  align-items: flex-start;
  padding: 1vw 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);

  &:last-child {
    border-bottom: none;
  }

  .spec-key {
    flex: 0 0 42%;
    font-size: 0.73vw;
    font-weight: 700;
    color: #fff;
    line-height: 1.6;
  }

  .spec-value {
    flex: 1;
    font-size: 0.68vw;
    color: #b8b8c8;
    line-height: 1.6;

    .spec-value-line {
      display: block;
    }
  }
}

// ========== Responsive ==========
@media (max-width: 768px) {
  .product-inner {
    flex-direction: column;
    gap: 24px;
  }
  .gallery { flex: none; width: 100%; }
  .gallery .thumb-item { width: 60px; height: 60px; border-radius: 8px; }
  .offer-tag { font-size: 11px; }
  .product-title { font-size: 28px; }
  .price-row .price-now { font-size: 18px; }
  .price-row .price-old { font-size: 13px; }
  .price-row .price-save { font-size: 11px; }
  .product-desc { font-size: 13px; }
  .feature-list .check-icon { width: 12px; height: 12px; }
  .feature-list .feature-title { font-size: 14px; }
  .feature-list .feature-sub { font-size: 12px; }
  .show-more-btn { font-size: 13px; }
  .service-row { flex-direction: column; gap: 16px; }
  .service-item .service-icon { width: 26px; height: 26px; }
  .service-item .service-title { font-size: 14px; }
  .service-item .service-sub { font-size: 12px; }
  .waitlist-title { font-size: 22px; }
  .waitlist-sub { font-size: 12px; }
  .waitlist-input { width: 52%; font-size: 13px; padding: 12px 16px; }
  .waitlist-btn { font-size: 13px; padding: 12px 20px; }
  .section-title { font-size: 22px; }
  .section-sub { font-size: 14px; }
  .cards-eyebrow { font-size: 14px; }
  .icons-brand { font-size: 13px; }
  .icons-grid { grid-template-columns: repeat(2, 1fr); max-width: 100%; gap: 24px; }
  .icon-cell .cell-icon { width: 32px; height: 32px; }
  .icon-cell .cell-label { font-size: 13px; }
  .video-wrap { border-radius: 12px; }
  .video-player { min-height: 200px; }
  .video-overlay .play-btn { width: 40px; }
  .video-overlay .play-text { font-size: 15px; }
  .cards-grid { grid-template-columns: 1fr; }
  .smart-card .smart-card-title { font-size: 16px; }
  .smart-card .smart-card-desc { font-size: 13px; }
  .specs-table { max-width: 100%; }
  .spec-row { padding: 12px 0; }
  .spec-row .spec-key { font-size: 13px; }
  .spec-row .spec-value { font-size: 12px; }
}
</style>
